import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material'
import { IconJoin } from '../../../icons/IconJoin'
import { IconUserPlaceholder } from '../../../icons/IconUserPlaceholder'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { useMarkNotificationsReadMutation } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import modalEmitter from '../../ModalComponent/modalEmitter'
import { toast } from 'react-toastify'
import { useApproveProjectJoinRequestMutation, useRejectProjectJoinRequestMutation } from '../../../api/projects'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug } from './notificationNav'
import { ProjectNameQuoted } from './notificationTypography'
import { NotificationTypeChip } from './NotificationTypeChip'
import { NotificationExecutedLine } from './NotificationExecutedLine'
import { notificationBodyWrapSx, notificationMessageClampSx } from './notificationTextClamp'
import { COLORS } from '../../../styles/variables'
import { extractApiErrorMessage } from '../../../utils/api'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.join_request.created' }>
}

type DecisionAction = 'approve' | 'reject'

function DecisionMessageModalBody(props: {
  action: DecisionAction
  projectId: number
  joinRequestId: number
  notificationId: number
  onResolved: (nextStatus: 'approved' | 'rejected') => void
}) {
  const { action, projectId, joinRequestId, notificationId, onResolved } = props
  const [message, setMessage] = React.useState('')
  const [approve, approveState] = useApproveProjectJoinRequestMutation()
  const [reject, rejectState] = useRejectProjectJoinRequestMutation()
  const [markRead] = useMarkNotificationsReadMutation()

  const isLoading = approveState.isLoading || rejectState.isLoading

  const handleConfirm = async () => {
    const decision_message = message.trim()
    try {
      if (action === 'approve') {
        await approve({
          projectId,
          joinRequestId,
          ...(decision_message.length > 0 ? { decision_message } : {}),
        }).unwrap()
        toast.success('Заявка принята')
        onResolved('approved')
      } else {
        await reject({
          projectId,
          joinRequestId,
          ...(decision_message.length > 0 ? { decision_message } : {}),
        }).unwrap()
        toast.success('Заявка отклонена')
        onResolved('rejected')
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось выполнить действие')
      return
    }

    try {
      await markRead([notificationId]).unwrap()
    } catch {
      // Не блокируем UX: основное действие уже выполнено.
    }

    modalEmitter.emitCloseModal()
  }

  return (
    <Stack gap={2}>
      <Typography variant="black-14-light">Сообщение (необязательно)</Typography>
      <TextField
        placeholder="Напишите причину или комментарий"
        multiline
        minRows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 500))}
        helperText={`${message.length}/500`}
        disabled={isLoading}
        fullWidth
      />
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button variant="follow" disabled={isLoading} onClick={() => modalEmitter.emitCloseModal()}>
          Отмена
        </Button>
        <Button
          variant={action === 'approve' ? 'action' : 'danger'}
          disabled={isLoading}
          onClick={handleConfirm}
        >
          Подтвердить
        </Button>
      </Stack>
    </Stack>
  )
}

export function JoinRequestCreatedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onUserClick, onProjectClick } = createNotificationNavHandlers(navigate)
  const [optimisticStatus, setOptimisticStatus] = React.useState<'approved' | 'rejected' | null>(null)
  const payload = notification.payload
  const project = payload?.project
  const actor = payload?.actor
  const join_request = payload?.join_request

  const projectTitle = (() => {
    const title = typeof project?.title === 'string' ? project.title.trim() : ''
    return title.length > 0 ? title : 'проект'
  })()
  const projectSlug = getProjectSlug(project)
  const actorName =
    typeof actor?.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'

  const projectId = Number(project?.id)
  const joinRequestId = Number(join_request?.id)

  const joinRequestStatusRaw = typeof join_request?.status === 'string' ? join_request.status : ''
  const joinRequestStatus = optimisticStatus ?? joinRequestStatusRaw
  const canAct =
    joinRequestStatus === 'pending' &&
    Number.isFinite(projectId) &&
    projectId > 0 &&
    Number.isFinite(joinRequestId) &&
    joinRequestId > 0

  const showExecutedLine = !canAct && Boolean(joinRequestStatus) && joinRequestStatus !== 'pending'

  const openDecisionModal = (action: DecisionAction) => {
    if (!canAct) return
    modalEmitter.emitOpenModal({
      dialog: {
        title: action === 'approve' ? 'Принять заявку' : 'Отклонить заявку',
        body: (
          <DecisionMessageModalBody
            action={action}
            projectId={projectId}
            joinRequestId={joinRequestId}
            notificationId={notification.id}
            onResolved={(nextStatus) => setOptimisticStatus(nextStatus)}
          />
        ),
        modalToCentrPosition: true,
      },
    })
  }

  const actorAvatarSrc = mediaSourceUrl(actor?.avatar_url) ?? undefined
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" gap={1.25} minWidth={0}>
        <Box
          onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
          sx={{ cursor: actorPathKey ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={actorName}
            avatarSrc={actorAvatarSrc}
            fallbackIcon={<IconUserPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={
              <>
                <Typography variant="black-12-light" component="span" sx={{ minWidth: 0, ...notificationBodyWrapSx }}>
                  {`в проект «${projectTitle}»`}
                </Typography>
                <IconJoin sx={{ width: 16, height: 16, flexShrink: 0, color: COLORS.CERULEAN }} />
                <NotificationTypeChip variant="join_request" />
              </>
            }
          />
        </Box>

        <Stack direction="column" minWidth={0} flex={1} gap={0.5}>
          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            Хочет стать участником <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} />
          </Typography>
          {typeof join_request?.message === 'string' && join_request.message.trim().length > 0 && (
            <Typography variant="black-12-light" sx={notificationMessageClampSx}>
              {join_request.message}
            </Typography>
          )}
          <Typography variant="black-12-light">Ожидает решения модератора проекта</Typography>

          {canAct && (
            <Stack direction="row" gap={1} sx={{ pt: 1 }}>
              <Button
                variant="action"
                onClick={(e) => {
                  e.stopPropagation()
                  openDecisionModal('approve')
                }}
              >
                Принять в проект
              </Button>
              <Button
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation()
                  openDecisionModal('reject')
                }}
              >
                Отклонить заявку
              </Button>
            </Stack>
          )}

          {showExecutedLine && <NotificationExecutedLine status={joinRequestStatus} />}

          <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
            {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  )
}
